/** Assigned-jobs listener helpers. Only assignJob writes active. No new callable. */

export const PWA_JOBS_ALIAS_PATH = '/app/jobs';

export type AssignedJob = {
    id: string;
    status?: string;
    [key: string]: unknown;
};

export function jobsFromAssignedSnapshot(querySnapshot: {
    forEach: (cb: (doc: { id: string; data: () => Record<string, unknown> }) => void) => void;
}): AssignedJob[] {
    const arrayJobs: AssignedJob[] = [];
    querySnapshot.forEach((documentSnapshot) => {
        arrayJobs.push({ ...documentSnapshot.data(), id: documentSnapshot.id });
    });
    return arrayJobs;
}

export function assignedJobIdsByStatus(jobs: ReadonlyArray<{ id?: string; status?: string }>, status: string): string[] {
    return jobs.filter((job) => job.status === status && job.id).map((job) => String(job.id));
}

function shouldFollowPwaFixtureTab(input: {
    fixture?: string | null;
    currentTab?: number | null;
    targetTab: number;
}): boolean {
    if (!input.fixture) return false;
    const tab = input.currentTab;
    return tab == null || tab === input.targetTab;
}

/** Open Active when assignJob adds an active id after the first snapshot — not on first load. */
export function shouldOpenActiveTab(input: {
    previousActiveIds?: readonly string[];
    nextActiveIds?: readonly string[];
    primed?: boolean;
    fixture?: string | null;
    currentTab?: number | null;
}): boolean {
    if (input.fixture === 'handoff') {
        return shouldFollowPwaFixtureTab({ fixture: input.fixture, currentTab: input.currentTab, targetTab: 2 });
    }
    if (!input.primed) return false;
    const previous = new Set(input.previousActiveIds || []);
    return (input.nextActiveIds || []).some((id) => id && !previous.has(id));
}

/** Open Complete when completeJob adds a complete id after the first snapshot — not on first load. */
export function shouldOpenCompleteTab(input: {
    previousCompleteIds?: readonly string[];
    nextCompleteIds?: readonly string[];
    primed?: boolean;
    fixture?: string | null;
    currentTab?: number | null;
}): boolean {
    if (input.fixture === 'complete-handoff') {
        return shouldFollowPwaFixtureTab({ fixture: input.fixture, currentTab: input.currentTab, targetTab: 3 });
    }
    if (!input.primed) return false;
    const previous = new Set(input.previousCompleteIds || []);
    return (input.nextCompleteIds || []).some((id) => id && !previous.has(id));
}

/** Focus / visibility cache-bust — skip fixtures, hidden tabs, and the first paint. */
export function shouldRefreshAssignedJobsOnFocus(input: {
    hidden?: boolean;
    ready?: boolean;
    fixture?: boolean;
}): boolean {
    if (input.fixture) return false;
    if (input.hidden) return false;
    return input.ready === true;
}

/**
 * Tab switch fires visibilitychange + focus together. One server get is enough —
 * a second get in the same beat is refresh thrash, not a missed completeJob.
 */
export function shouldCoalesceAssignedJobsFocusRefresh(input: {
    lastAt?: number;
    now?: number;
    windowMs?: number;
} = {}): boolean {
    const last = input.lastAt ?? 0;
    const now = input.now ?? 0;
    if (!last) return true;
    return now - last >= (input.windowMs ?? 750);
}

export function assignedJobsQuery(
    jobsRef: {
        where: (
            field: string,
            op: string,
            value: string,
        ) => { where: (field: string, op: string, value: string) => unknown };
    },
    techId: string,
    companyId: string,
) {
    return jobsRef.where('companyId', '==', companyId).where('assignee', '==', techId);
}
